import { Injectable } from '@angular/core';
import { PublicKey } from '@solana/web3.js';
import { SPLToken } from '@neonevm/token-transfer-core';
import { throttleTime } from 'rxjs/operators';
import {
  catchError,
  combineLatest,
  debounceTime,
  delay,
  filter,
  forkJoin,
  from,
  map,
  Observable,
  of,
  ReplaySubject,
  SubscriptionLike,
  switchMap,
  tap
} from 'rxjs';
import { Address, TransferDirection, TransferToken } from '../../models';
import { CHAIN_ID, itemUnsubscribe, NEON, SOL, W_NEON, W_SOL } from '../../utils';
import { ApiService } from '../../api/api.service';
import { SolanaWalletService } from './solana-wallet.service';
import { WalletConnectService } from './wallet-connect.service';
import { ProxyStatusService } from './proxy-status.service';

@Injectable({ providedIn: 'root' })
export class TokensListService {
  chainId = CHAIN_ID;
  private _tokens: TransferToken[] = [];
  private _tokens$: ReplaySubject<TransferToken[]> = new ReplaySubject<TransferToken[]>(0);
  private sub: SubscriptionLike[] = [];

  get tokens$(): Observable<TransferToken[]> {
    return this._tokens$;
  }

  neonTokensBalance(): Observable<TransferToken[]> {
    return combineLatest([this.tokens$, this.neon.address$]).pipe(
      filter(([tokens]) => tokens.length > 0),
      switchMap(([tokens, address]) => {
        if (address?.length > 0) {
          const requests = tokens.map(token => this.mintBalance(token, address));
          return forkJoin(requests);
        }
        tokens.map(token => token.neonReset());
        return of(tokens).pipe(delay(100));
      }));
  }

  solanaTokensBalance(): Observable<TransferToken[]> {
    return combineLatest([this.tokens$, this.solana.publicKey$]).pipe(
      debounceTime(100),
      filter(([tokens]) => tokens.length > 0),
      switchMap(([tokens, address]) => {
        if (address instanceof PublicKey) {
          return this.api.solanaBalances(address).pipe(map(items => {
            for (const item of items) {
              const id = this._tokens.findIndex(i => i.token.address_spl === item.mint);
              if (id > -1) {
                const token = this._tokens[id];
                token.solanaUpdate({ amount: item.tokenAmount.amount, decimals: item.tokenAmount.decimals });
              }
            }
            return tokens;
          }));
        }
        tokens.map(token => token.solanaReset());
        return of(tokens).pipe(delay(100));
      }));
  }

  mintBalance(token: TransferToken, address: Address): Observable<TransferToken> {
    let methodForNativeToken: Observable<unknown>;
    let method: Observable<unknown>;
    let native = false;
    switch (token.token.symbol) {
      case NEON: {
        native = true;
        methodForNativeToken = this.neon.neonBalance();
        break;
      }
      case W_NEON: {
        method = from(this.neon.wNeonBalance(token.token));
        break;
      }
      default: {
        if (token.token.symbol === SOL && this.proxy.gasToken$.value?.tokenName === SOL) {
          native = true;
          methodForNativeToken = this.neon.neonBalance();
          break;
        } else {
          method = from(this.neon.tokenBalance(token.token));
        }
      }
    }

    // @ts-ignore
    const callableMethod = methodForNativeToken || method;
    return callableMethod.pipe(map((d: any) => {
        const decimals = native ? d.decimals : token.token.decimals;
        token.neonUpdate({ amount: BigInt(d.value), decimals });
        return token;
      }),
      catchError(() => {
        token.neonUpdate({ amount: 0n, decimals: token.token.decimals });
        return of(token);
      }));
  }

  splBalance(token: TransferToken, pubkey: PublicKey): Observable<TransferToken> {
    let method: Observable<unknown>;
    if (token.token.address_spl) {
      const mintAccount = new PublicKey(token.token.address_spl);
      method = this.api.solanaBalance(this.solana.publicKey, mintAccount).pipe(map(t => t.tokenAmount));
    } else {
      method = of({ amount: 0, decimals: token.token.decimals });
    }
    return method.pipe(map((d) => {
      // @ts-ignore
      token.solanaUpdate({ amount: BigInt(d.amount), decimals: d.decimals });
      return token;
    }), catchError(_ => {
      token.solanaUpdate({ amount: 0n, decimals: token.token.decimals });
      return of(token);
    }));
  }

  tokenBalance(token: TransferToken): Observable<TransferToken> {
    return combineLatest([this.neon.address$, this.solana.publicKey$]).pipe(
      switchMap(([n, s]) => {
        const result = [];
        if (n) {
          switch (token.token.symbol) {
            case W_NEON:
              this.findTokenBalance(NEON, (t) => result.push(this.mintBalance(t, n)));
              break;
            default:
              result.push(this.mintBalance(token, n));
              break;
          }
        }
        if (s) {
          switch (token.token.symbol) {
            case SOL:
            case W_SOL:
              this.findTokenBalance(SOL, (t) => result.push(this.splBalance(t, s)));
              this.findTokenBalance(W_SOL, (t) => result.push(this.splBalance(t, s)));
              break;
            case W_NEON:
              this.findTokenBalance(NEON, (t) => result.push(this.splBalance(t, s)));
              break;
            default:
              result.push(this.splBalance(token, s));
              break;
          }
        }
        return forkJoin(result);
      }),
      map(() => token));
  }

  init(): void {
    this.sub.push(this.api.tokens().pipe(tap(this.tokenListData)).subscribe());
    this.sub.push(this.neon.refresh$.pipe(debounceTime(1500), switchMap(() => this.neonTokensBalance())).subscribe());
    this.sub.push(this.solana.refresh$.pipe(throttleTime(1500), switchMap(() => this.solanaTokensBalance())).subscribe());
  }

  destroy(): void {
    itemUnsubscribe(this.sub);
  }

  tokensOrderByBalance = (tokens: TransferToken[], direction: TransferDirection): TransferToken[] => {
    const b: TransferToken[] = [];
    const e: TransferToken[] = [];
    for (const token of tokens) {
      if (token[direction].value.balance?.amount) {
        b.push(token);
      } else {
        e.push(token);
      }
    }
    return b.concat(e);
  };

  private findTokenBalance = (symbol: string, method: (token: TransferToken) => void): void => {
    const id = this._tokens.findIndex(i => i.token.symbol === symbol);
    if (id > -1) {
      method(this._tokens[id]);
    }
  };

  private tokenListData = (tokenList: SPLToken[]): void => {
    const tokens = [...tokenList];
    this._tokens = tokens.map(t => new TransferToken(t));
    this._tokens$.next(this._tokens);
  };

  constructor(private neon: WalletConnectService, private solana: SolanaWalletService, private proxy: ProxyStatusService, private api: ApiService) {
  }
}
